<script>
  import { onMount } from 'svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import WorkflowForm from './workflows/WorkflowForm.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faSquarePlus, faRotate } from '@fortawesome/pro-light-svg-icons';

  const API_BASE_URL = '/api/proxy';

  let items = [];
  let loading = true;
  let error = null;
  let totalPages = 1;
  let currentPage = 1;
  let pageSize = 20;
  let totalItems = 0;
  let sortField = 'name';
  let sortOrder = 'asc';
  let active = '';

  let showForm = false;
  let editWorkflow = null;

  let showMenu = false;
  let menuItem = null;

  onMount(() => {
    const params = new URLSearchParams(window.location.search);
    currentPage = parseInt(params.get('page') || '1', 10);
    pageSize = parseInt(params.get('pageSize') || '20', 10);
    sortField = params.get('sortField') || 'name';
    sortOrder = params.get('sortOrder') || 'asc';
    active = params.get('active') ?? '';
    fetchItems();
  });

  function updateUrl() {
    const params = new URLSearchParams({
      page: String(currentPage),
      pageSize: String(pageSize),
      sortField,
      sortOrder
    });
    if (active !== '') params.set('active', String(active));
    const query = params.toString();
    history.replaceState(null, '', `${window.location.pathname}?${query}`);
  }

  async function fetchItems() {
    try {
      loading = true;
      error = null;
      const params = new URLSearchParams();
      params.set('page', String(currentPage));
      params.set('pageSize', String(pageSize));
      params.set('sortField', sortField);
      params.set('sortOrder', sortOrder);
      if (active !== '') params.set('active', String(active));
      const resp = await fetch(`${API_BASE_URL}/workflows?${params.toString()}`);
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.message || 'Failed to load workflows');
      items = data?.data || [];
      totalItems = data?.pagination?.total || 0;
      totalPages = data?.pagination?.totalPages || 1;
    } catch (e) {
      console.error('fetchItems error:', e);
      // If we can't fetch (e.g., collection hasn't been created yet), show empty state without an error banner
      items = [];
      totalItems = 0;
      totalPages = 1;
      // Keep error null to avoid alarming banner for empty/first-run state
      error = null;
    } finally {
      loading = false;
    }
  }

  function handleSort(field) {
    if (sortField === field) {
      sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      sortField = field;
      sortOrder = 'asc';
    }
    updateUrl();
    fetchItems();
  }

  function handleFilter() {
    currentPage = 1;
    updateUrl();
    fetchItems();
  }

  function handleReset() {
    active = '';
    sortField = 'name';
    sortOrder = 'asc';
    currentPage = 1;
    updateUrl();
    fetchItems();
  }

  function handleRefresh() {
    fetchItems();
  }

  function openCreateModal() {
    editWorkflow = null;
    showForm = true;
  }

  function openEditModal(item) {
    editWorkflow = item;
    showForm = true;
  }

  async function handleSubmit(event) {
    const wf = event.detail;
    try {
      const url = editWorkflow 
        ? `${API_BASE_URL}/workflows/${wf._id}`
        : `${API_BASE_URL}/workflows`;
      const method = editWorkflow ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: wf.name,
          description: wf.description,
          version: wf.version,
          active: wf.active,
          required: wf.required,
          tasks: (wf.tasks || []).map((t, idx) => ({
            _id: t._id,
            title: t.title,
            s3Key: t.s3Key,
            description: t.description,
            sortOrder: typeof t.sortOrder === 'number' ? t.sortOrder : idx
          }))
        })
      });
      if (!(response.status >= 200 && response.status < 300)) {
        let serverMsg = '';
        try { serverMsg = (await response.json())?.message || ''; } catch { serverMsg = await response.text(); }
        throw new Error(serverMsg || `Failed to ${editWorkflow ? 'update' : 'create'} workflow (${response.status})`);
      }
      error = null;
      showForm = false;
      fetchItems();
    } catch (e) {
      error = e.message;
      console.error('handleSubmit error:', e);
    }
  }

  async function handleDelete(id) {
    if (!confirm('Delete this workflow?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/workflows/${id}`, { method: 'DELETE' });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || 'Failed to delete');
      fetchItems();
    } catch (e) {
      error = e.message;
      console.error('handleDelete error:', e);
    }
  }

  function handlePageChange(page) {
    currentPage = page;
    updateUrl();
    fetchItems();
  }

  function handleMenuClick(item, event) {
    event.stopPropagation();
    menuItem = item;
    showMenu = !showMenu;
  }

  function handleMenuClose() {
    showMenu = false;
    menuItem = null;
  }
</script>

<WorkflowForm {showForm} {editWorkflow} on:submit={handleSubmit} on:close={() => (showForm = false)} />

<StudyHeader />

<FeatureInfo 
  title="Workflows" 
  description="Define ordered tasks (S3 files) a user must view and confirm."
  marginBottom="mb-0"
/>

<Toolbar title="Workflows" itemCountTitle="Workflows" itemCount={totalItems}>
  <div class="flex items-center gap-4">
    <div class="text-m mb-0">
      Status
      <select
        bind:value={active}
        on:change={handleFilter}
        class="h-6 w-32 ms-1 px-1 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
      >
        <option value="">All</option>
        <option value="true">Active</option>
        <option value="false">Inactive</option>
      </select>
    </div>

    <button
      on:click={openCreateModal}
      class="pagination mx-1 p-0"
      title="Add workflow"
    >
      <FontAwesomeIcon icon={faSquarePlus} />
    </button>
    <button on:click={handleRefresh} class="pagination mx-1 p-0" title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

{#if error}
  <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 mx-4">{error}</div>
{/if}

{#if loading}
  <div class="flex justify-center items-center py-4">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
  </div>
{:else}
  <div class="px-1 pt-0">
    {#if (items || []).length === 0}
      <div class="text-center py-6 text-gray-500 dark:text-gray-400">No workflows defined yet.</div>
    {:else}
      <table class="min-w-full border-collapse table-auto">
        <thead>
          <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
            <th class="px-4 py-2 text-left bold cursor-pointer" on:click={() => handleSort('name')}>Name</th>
            <th class="px-4 py-2 text-left bold">Version</th>
            <th class="px-4 py-2 text-left bold">Tasks</th>
            <th class="px-4 py-2 text-left bold cursor-pointer" on:click={() => handleSort('active')}>Active</th>
            <th class="px-4 py-2 text-left bold cursor-pointer" on:click={() => handleSort('required')}>Required</th>
            <th class="px-4 py-2 text-left bold">Updated</th>
            <th class="w-8 px-4 py-2 text-left bold">&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {#each items as item, index}
            <tr class="text-sm text-gray-800 dark:text-slate-300 {index % 2 === 0 ? 'bg-stone-50 dark:bg-gray-725' : 'bg-stone-75 dark:bg-gray-750'}">
              <td class="px-4 py-2">
                <div class="font-medium">{item.name}</div>
                {#if item.description}
                  <div class="text-xs text-gray-500">{item.description}</div>
                {/if}
              </td>
              <td class="px-4 py-2">{item.version || ''}</td>
              <td class="px-4 py-2">
                {(item.tasks || []).length}
              </td>
              <td class="px-4 py-2">
                {item.active ? 'Yes' : 'No'}
              </td>
              <td class="px-4 py-2">
                {item.required ? 'Yes' : 'No'}
              </td>
              <td class="px-4 py-2">{item.updatedAt ? new Date(item.updatedAt).toLocaleString() : ''}</td>
              <td class="px-4 py-2">
                <div class="menu-container relative">
                  <button on:click={(e) => handleMenuClick(item, e)} class="pagination mx-1 p-0" title="More options">⋯</button>
                  {#if showMenu && menuItem?._id === item._id}
                    <div class="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50">
                      <button class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700" on:click={() => { openEditModal(item); handleMenuClose(); }}>Edit</button>
                      <button class="w-full text-left px-4 py-2 text-sm text-red-700 dark:text-red-300 hover:bg-gray-100 dark:hover:bg-gray-700" on:click={() => { handleDelete(item._id); handleMenuClose(); }}>Delete</button>
                    </div>
                  {/if}
                </div>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>

      {#if totalPages > 1}
        <div class="flex justify-center items-center py-2">
          <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
        </div>
      {/if}
    {/if}
  </div>
{/if}


